import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { entero, fechaHora } from '../lib/formato.js';
import { CLS_MOV, txt, TXT_MOV } from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';
import { useMask, useSesion } from '../piezas/Sesion.jsx';

/**
 * El libro de movimientos.
 *
 * Es una bitacora, no una tabla editable: el inventario se mueve por la
 * puerta unica del servidor y aqui solo se lee. Por eso no hay boton de alta
 * ni de edicion, y por eso el orden es siempre el cronologico inverso — lo
 * que se pregunta de un libro es "que paso al final".
 */

interface Movimiento extends Record<string, unknown> {
  folio: string;
  tipo: string;
  creado_en: string;
  comentario: string | null;
  origen: string | null;
  destino: string | null;
  ticket_folio: string | null;
  numero_guia: string | null;
  actor: string | null;
  renglones: number;
  piezas: number;
  valor_centavos?: number;
}

export function Movimientos(): JSX.Element {
  const [filas, setFilas] = useState<Movimiento[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tipo, setTipo] = useState('');
  const { veMontos } = useSesion();
  const mask = useMask();

  useEffect(() => {
    api
      .obtener<Movimiento[]>('/v1/movimientos?limite=200')
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, []);

  /* Los chips se derivan de los tipos QUE APARECEN, no de la lista completa
     de ocho: ofrecer un filtro que siempre devuelve cero es peor que no
     ofrecerlo. */
  const tipos = useMemo(() => {
    if (filas === null) return [];
    const cuenta = new Map<string, number>();
    for (const f of filas) cuenta.set(f.tipo, (cuenta.get(f.tipo) ?? 0) + 1);
    return [...cuenta.entries()].sort((a, b) => b[1] - a[1]);
  }, [filas]);

  const vistas = filas === null ? null : tipo === '' ? filas : filas.filter((f) => f.tipo === tipo);

  return (
    <>
      <Pagehead />

      {tipos.length > 1 && (
        <div className="fila wrap" style={{ marginBottom: 12 }}>
          <div className="chips">
            <button
              type="button"
              className={tipo === '' ? 'chip on' : 'chip'}
              onClick={() => setTipo('')}
              data-prueba="filtro-todos"
            >
              Todos
            </button>
            {tipos.map(([t, n]) => (
              <button
                key={t}
                type="button"
                className={tipo === t ? 'chip on' : 'chip'}
                onClick={() => setTipo(t)}
                data-prueba={`filtro-${t}`}
              >
                {txt(TXT_MOV, t)} <span className="muted">{n}</span>
              </button>
            ))}
          </div>
          <span className="crece" />
          {vistas && (
            <span className="muted" style={{ fontSize: 12 }} data-prueba="total">
              {vistas.length} {vistas.length === 1 ? 'movimiento' : 'movimientos'}
            </span>
          )}
        </div>
      )}

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {vistas === null ? (
          <Cargando que="el libro de movimientos" />
        ) : vistas.length === 0 ? (
          <Vacio
            mensaje={tipo === '' ? 'El libro esta vacio' : 'Ningun movimiento de ese tipo'}
            ayuda="Cada entrada, salida o traspaso queda registrado aqui en firme."
            icono="📜"
          />
        ) : (
          <table data-prueba="tabla-movimientos">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Tipo</th>
                <th>Ruta</th>
                <th className="num">Piezas</th>
                {veMontos && <th className="num sec">Valor</th>}
                <th className="sec">Referencia</th>
                <th>Cuando</th>
              </tr>
            </thead>
            <tbody>
              {vistas.map((m) => (
                <tr key={m.folio}>
                  <td className="folio">{m.folio}</td>
                  <td>
                    <span className={`tag ${CLS_MOV[m.tipo] ?? 't-mute'}`}>
                      {txt(TXT_MOV, m.tipo)}
                    </span>
                  </td>
                  <td>
                    {/* Un movimiento tiene origen, destino o los dos: una
                        entrada no tiene origen y una salida no tiene destino.
                        La flecha se dibuja solo cuando hay dos puntas. */}
                    {m.origen && m.destino ? (
                      <>
                        <span className="folio">{m.origen}</span> →{' '}
                        <span className="folio">{m.destino}</span>
                      </>
                    ) : m.destino ? (
                      <>
                        entra a <span className="folio">{m.destino}</span>
                      </>
                    ) : m.origen ? (
                      <>
                        sale de <span className="folio">{m.origen}</span>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                    {m.comentario && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {m.comentario}
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {entero(m.piezas)}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {m.renglones} {m.renglones === 1 ? 'renglon' : 'renglones'}
                    </div>
                  </td>
                  {veMontos && <td className="num sec">{mask(m.valor_centavos)}</td>}
                  <td className="sec">
                    {m.ticket_folio && (
                      <Link className="folio" to={`/tickets/${m.ticket_folio}`}>
                        {m.ticket_folio}
                      </Link>
                    )}
                    {m.numero_guia && (
                      <div className="folio muted" style={{ fontSize: 11 }}>
                        {m.numero_guia}
                      </div>
                    )}
                    {!m.ticket_folio && !m.numero_guia && <span className="muted">—</span>}
                  </td>
                  <td>
                    {fechaHora(m.creado_en)}
                    {m.actor && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {m.actor}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
